import React, { useState } from "react";
import { Phone, Mail, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "House Washing", label: "House Washing" },
  { value: "Driveway Washing", label: "Driveway Washing" },
  { value: "Gutter Cleaning", label: "Gutter Cleaning" },
  { value: "Multiple Services", label: "Multiple Services" },
];

export const QuoteForm = React.forwardRef((props, ref) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      toast.success("Quote request sent! We'll call you within 1 hour.");
      setForm({ name: "", email: "", phone: "", service: "", address: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-slate-900 text-white scroll-mt-20 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 -z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/5130aa54-ea2a-4a8c-96eb-675ebbe6fdda/images/2167a1596db1b8a77b7fb269758abcf54697d095e07cc1296427edddab320f9a.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div aria-hidden className="absolute inset-0 bg-slate-900/80 -z-0" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-16">
        {/* Left info */}
        <div className="lg:col-span-2">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-sky-300">
            Free Quote
          </p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Tell us about your <span className="text-sky-300">project.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-300 font-medium leading-relaxed">
            Most quotes come back within an hour. No pressure, no upsell — just
            a fair price and a clear plan.
          </p>

          <div className="mt-10 space-y-5">
            <InfoLine
              icon={<Phone className="h-5 w-5" />}
              label="Call us directly"
              value={SITE.phone}
              href={SITE.phoneHref}
              testid="info-phone"
            />
            <InfoLine
              icon={<Mail className="h-5 w-5" />}
              label="Email"
              value={SITE.email}
              href={`mailto:${SITE.email}`}
              testid="info-email"
            />
            <InfoLine
              icon={<MapPin className="h-5 w-5" />}
              label="Service area"
              value="All of Ohio"
              testid="info-area"
            />
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-3">
          <div className="bg-white text-slate-900 rounded-3xl p-6 md:p-10 shadow-2xl ring-1 ring-white/10">
            {success ? (
              <div data-testid="quote-success" className="py-10 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-blue-600 text-white grid place-items-center">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="font-display mt-5 text-2xl md:text-3xl font-extrabold">
                  Quote request received!
                </h3>
                <p className="mt-3 text-slate-600 font-medium">
                  We'll be in touch within the hour. For urgent jobs, call us at{" "}
                  <a href={SITE.phoneHref} className="text-blue-600 font-bold">
                    {SITE.phone}
                  </a>
                  .
                </p>
                <Button
                  data-testid="quote-success-reset"
                  onClick={() => setSuccess(false)}
                  className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl"
                >
                  Send another request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="quote-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name *">
                    <Input
                      data-testid="quote-name"
                      value={form.name}
                      onChange={(e) => update("name")(e.target.value)}
                      placeholder="Jane Doe"
                      required
                      className="bg-slate-50 border-slate-200 focus-visible:ring-blue-500 rounded-xl h-11"
                    />
                  </Field>
                  <Field label="Phone *">
                    <Input
                      data-testid="quote-phone"
                      value={form.phone}
                      onChange={(e) => update("phone")(e.target.value)}
                      placeholder="(234) 555-0100"
                      required
                      className="bg-slate-50 border-slate-200 focus-visible:ring-blue-500 rounded-xl h-11"
                    />
                  </Field>
                </div>

                <Field label="Email *">
                  <Input
                    data-testid="quote-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email")(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-slate-50 border-slate-200 focus-visible:ring-blue-500 rounded-xl h-11"
                  />
                </Field>

                <Field label="Service needed *">
                  <Select
                    value={form.service}
                    onValueChange={update("service")}
                  >
                    <SelectTrigger
                      data-testid="quote-service"
                      className="bg-slate-50 border-slate-200 rounded-xl h-11"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          data-testid={`quote-service-${s.value.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Property address">
                  <Input
                    data-testid="quote-address"
                    value={form.address}
                    onChange={(e) => update("address")(e.target.value)}
                    placeholder="123 Main St, Akron OH"
                    className="bg-slate-50 border-slate-200 focus-visible:ring-blue-500 rounded-xl h-11"
                  />
                </Field>

                <Field label="Tell us about the project">
                  <Textarea
                    data-testid="quote-message"
                    value={form.message}
                    onChange={(e) => update("message")(e.target.value)}
                    placeholder="Square footage, special concerns, preferred date..."
                    rows={4}
                    className="bg-slate-50 border-slate-200 focus-visible:ring-blue-500 rounded-xl"
                  />
                </Field>

                <Button
                  type="submit"
                  data-testid="quote-submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 rounded-xl shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Request my free quote"
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center font-medium">
                  We respond in under 1 hour during business hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

QuoteForm.displayName = "QuoteForm";

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5 block">
      {label}
    </Label>
    {children}
  </div>
);

const InfoLine = ({ icon, label, value, href, testid }) => {
  const inner = (
    <div className="flex items-center gap-4">
      <div className="h-11 w-11 rounded-xl bg-blue-600 text-white grid place-items-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
          {label}
        </div>
        <div className="font-display text-lg font-bold text-white">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block hover:text-sky-300 transition-colors">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
};
